import {
  AgeLimit,
  CampaignCategory,
  CampaignCostHandlingMethod,
  CampaignRequirementOptions,
  CampaignSnsRequirementSnsPostType,
  ExpenseEvidenceType,
  LanguageType,
  PrTargetAge,
  PrTargetGender,
  SearchGenre,
} from '~/generated/graphql'
import { Plan } from './Plan'
import { type BurdenRate } from '~/features/campaign_input/constants/burdenRate'

export type CampaignCoverImage = {
  url: string
  w120xh120?: { url: string }
  w480xh480?: { url: string }
}

export const CampaignSnsRequirementSnsKind = {
  Instagram: 'instagram',
  NoSettings: 'no_settings',
  TikTok: 'tiktok',
  Youtube: 'youtube',
  Twitter: 'twitter',
} as const

export type CampaignSnsRequirementSnsKind =
  (typeof CampaignSnsRequirementSnsKind)[keyof typeof CampaignSnsRequirementSnsKind]

export const CampaignSnsRequirementSnsPostTypeKind = {
  InstagramStories: 'instagram_stories',
  NoSettings: 'no_settings',
  SnsDefault: 'sns_default',
} as const

export type CampaignSnsRequirementSnsPostTypeKind =
  (typeof CampaignSnsRequirementSnsPostTypeKind)[keyof typeof CampaignSnsRequirementSnsPostTypeKind]

export type CampaignSnsRequirements = {
  id: string
  requestUrl?: string | null
  snsType: CampaignSnsRequirementSnsKind
  postFooter: string
  tagList: string
}

export const CampaignStatusKind = {
  //審査中
  ApprovalRequest: 'approval_request',
  //修正待ち。審査NGだが、修正内容（LINEで別途共有される）に対応すれば審査に再提出できる
  RevisionRequired: 'revision_required',
  // 否認済み。審査NGであり、修正することもできない
  Denial: 'denial',
  // 審査OK。決められた募集開始日時を迎えたらベースアプリ上で公開が始まる
  Approved: 'approved',
  // 終了済み
  Finish: 'finish',
} as const
export type CampaignStatusKind = (typeof CampaignStatusKind)[keyof typeof CampaignStatusKind]

export const CampaignReservationKind = {
  NotUse: 'other',
  ScheduleAndTicket: 'discuss',
  Schedule: 'schedule_only',
  Ticket: 'ticket_only',
} as const
export type CampaignReservationKind = (typeof CampaignReservationKind)[keyof typeof CampaignReservationKind]

export const CampaignAgeKind = {
  All: 'all_age',
  Teen: 'teen',
  Twenties: 'twenties',
  OverThirties: 'over_thirties',
} as const
export type CampaignAgeKind = (typeof CampaignAgeKind)[keyof typeof CampaignAgeKind]

export const CampaignAgeLimit = {
  NoLimit: 'no_limit',
  OverTwenties: 'over_twenties',
} as const
export type CampaignAgeLimit = (typeof CampaignAgeLimit)[keyof typeof CampaignAgeLimit]
export const CampaignAgeLimitLabel = {
  [AgeLimit.NoLimit]: '応募対象に含める',
  [AgeLimit.OverTwenties]: '応募対象に含めない',
}
export const CampaignCompanionKind = {
  NoAccompanying: 'no_accompanying',
  Accompanying: 'accompanying',
} as const
export type CampaignCompanionKind = (typeof CampaignCompanionKind)[keyof typeof CampaignCompanionKind]

export const CampaignGenderKind = {
  NotKnown: 'not_known',
  Man: 'man',
  Woman: 'woman',
} as const
export type CampaignGenderKind = (typeof CampaignGenderKind)[keyof typeof CampaignGenderKind]

export const CampaignMassageTherapistGender = {
  Unspecified: '指定なし',
  Man: '男性',
  Woman: '女性',
  Unknown: '当日までわからない',
} as const
export type CampaignMassageTherapistGender =
  (typeof CampaignMassageTherapistGender)[keyof typeof CampaignMassageTherapistGender]

export const CampaignProjectType = {
  Monitor: 'monitor',
  Posted: 'posted',
} as const
export type CampaignProjectType = (typeof CampaignProjectType)[keyof typeof CampaignProjectType]

// TODO: AdvAccount型のstatusと共通？調査
export const CampaignAdvAccountStatus = {
  AccountPublished: 'account_published',
  AccountNotPublished: 'account_not_published',
} as const
export type CampaignAdvAccountStatus = (typeof CampaignAdvAccountStatus)[keyof typeof CampaignAdvAccountStatus]

export const CampaignDesiredFollowers = {
  SelfInput: 'self_input',
  FewFollowers: 'few_followers',
  ManyFollowers: 'many_followers',
} as const
export type CampaignDesiredFollowers = (typeof CampaignDesiredFollowers)[keyof typeof CampaignDesiredFollowers]

export const CampaignMediaType = {
  AllMedia: 'all_media',
  Picture: 'picture',
  Movie: 'movie',
  PictureAndMovie: 'picture_and_movie',
  MediaNone: 'media_none',
} as const
export type CampaignMediaType = (typeof CampaignMediaType)[keyof typeof CampaignMediaType]

export const CampaignScheduleType = {
  NotUsed: 'not_used',
  ReservableSchedule: 'reservable_schedule',
  OldCampaign: null, // 日程調整リニューアルよりも前のキャンペーンはnull
} as const
export type CampaignScheduleType = (typeof CampaignScheduleType)[keyof typeof CampaignScheduleType]

export const RegularlyPublishedStatus = {
  On: 'on',
  Off: 'off',
} as const
export type RegularlyPublishedStatus = (typeof RegularlyPublishedStatus)[keyof typeof RegularlyPublishedStatus]

export const CampaignPrTargetAgeLabel = {
  [PrTargetAge.Age_17AndUnder]: '17歳以下',
  [PrTargetAge.Age_18To_24]: '18〜24歳',
  [PrTargetAge.Age_25To_34]: '25〜34歳',
  [PrTargetAge.Age_35To_44]: '35〜44歳',
  [PrTargetAge.Age_45To_54]: '45〜54歳',
  [PrTargetAge.Age_55AndAbove]: '55歳以上',
} as const

export const CampaignPrTargetGenderLabel = {
  [PrTargetGender.Female]: '女性',
  [PrTargetGender.Male]: '男性',
  [PrTargetGender.FemaleAndMale]: '女性と男性',
  [PrTargetGender.None]: '指定なし',
} as const

export const CampaignDesiredLanguageLabel = {
  [LanguageType.Japanese]: '日本語',
  [LanguageType.English]: '英語',
  [LanguageType.Chinese]: '中国語',
} as const

export const CampaignGenreLabel = {
  [SearchGenre.Apparel]: 'アパレル',
  [SearchGenre.Beauty]: 'ビューティー',
  [SearchGenre.Gourmet]: 'グルメ',
  [SearchGenre.Leisure]: 'レジャー',
  [SearchGenre.Travel]: 'トラベル',
  [SearchGenre.Lifestyle]: 'ライフスタイル',
} as const

export const CompositionOptions = [
  CampaignRequirementOptions.CompositionInfluencerAndService,
  CampaignRequirementOptions.CompositionProductOnly,
  CampaignRequirementOptions.CompositionUsageInContext,
] as const

export const CampaignSnsPostTypeListLabel = {
  [CampaignSnsRequirementSnsPostType.InstagramFeed]: 'フィード',
  [CampaignSnsRequirementSnsPostType.InstagramReel]: 'リール',
  [CampaignSnsRequirementSnsPostType.InstagramStories]: 'ストーリーズ',
  [CampaignSnsRequirementSnsPostType.NoSettings]: '設定なし',
  [CampaignSnsRequirementSnsPostType.SnsDefault]: 'デフォルト方式',
} as const

export const CampaignRequirementOptionsLabel = {
  [CampaignRequirementOptions.DraftCheck]: '下書きチェック',
  [CampaignRequirementOptions.SecondaryUse]: '二次利用',
  [CampaignRequirementOptions.SpecifiedPrText]: '文言指定',
  [CampaignRequirementOptions.CompositionInfluencerAndService]:
    'インフルエンサーとサービス（商品）を一緒に写してほしい',
  [CampaignRequirementOptions.CompositionProductOnly]: '商品単体で写してほしい',
  [CampaignRequirementOptions.CompositionUsageInContext]: '実際に体験しているところを写してほしい',
} as const

/**
 * キャンペーン要件オプションの単価。
 *
 * 単価はDBに保存しておらず、過去に作成されたキャンペーンの表示金額・請求額も
 * すべてこの定数を参照している。そのためここを変更すると、過去キャンペーンの
 * 表示金額も新しい単価に変わる。
 *
 * 単価を改定する場合は、作成時点の単価をDBに保存する対応
 * （campaign_campaign_requirement_options.price）が必要。設計と検討経緯は
 * Linear プロジェクト「報酬の細分化 - オプション単価のDB保存対応」を参照。
 * https://linear.app/toridori/project/報酬の細分化-オプション単価のdb保存対応-932d5fac97df
 */
export const OptionPricesLabel = {
  [CampaignRequirementOptions.DraftCheck]: 1000,
  [CampaignRequirementOptions.SecondaryUse]: 5000,
  [CampaignRequirementOptions.SpecifiedPrText]: 1000,
  [CampaignRequirementOptions.CompositionInfluencerAndService]: 1000,
  [CampaignRequirementOptions.CompositionProductOnly]: 1000,
  [CampaignRequirementOptions.CompositionUsageInContext]: 1000,
} as const

export const LegacyCampaignCostHandlingMethod = {
  NoChargeCampaign: 'no_charge_campaign',
  ExpenseCampaign: 'expense_campaign',
} as const

export type LegacyCampaignCostHandlingMethod =
  (typeof LegacyCampaignCostHandlingMethod)[keyof typeof LegacyCampaignCostHandlingMethod]

export const CostHandlingMethodLavel = {
  [CampaignCostHandlingMethod.ExpenseCampaign]: '立替型',
  [CampaignCostHandlingMethod.NoChargeCampaign]: '無償提供型',
} as const

/**
 * キャンペーンの精算方法ラベルを取得（立替型の場合は還元率を含む）
 * @param method - 精算方法
 * @param influencerBurdenRate - インフルエンサー負担率（0, 10, 20, 30）
 * @returns ラベル文字列
 */
export const getCostHandlingMethodLabel = (
  method: CampaignCostHandlingMethod,
  influencerBurdenRate?: BurdenRate | null,
): string => {
  if (method === CampaignCostHandlingMethod.NoChargeCampaign) {
    return '無償提供型'
  }

  // 立替型の場合、還元率を計算して表示
  const rate = influencerBurdenRate ?? 0
  const returnRate = 100 - rate
  return `立替型(${returnRate}%還元)`
}

export const ExpenseEvidenceTypeLabel = {
  [ExpenseEvidenceType.Receipt]: '領収書',
  [ExpenseEvidenceType.OrderNumber]: '注文番号',
} as const

export const SubGenreLabel = {
  1: 'カフェ',
  2: '食べ歩き',
  3: '洋食',
  4: '和食',
  5: '韓国料理',
  6: '中華',
  7: 'ラーメン',
  8: 'イタリアン',
  9: 'フレンチ',
  10: 'スイーツ',
  11: '手料理・アレンジレシピ',
  12: 'バー',
  13: 'カレー',
  14: '海鮮',
  15: '居酒屋',
  53: 'エスニック料理',
  16: 'その他',
  17: 'キャンプ・グランピング',
  18: 'ホテル・旅館',
  19: 'その他',
  20: 'アウトドア用品',
  21: 'ドライブ・ツーリング',
  22: 'アクティビティ',
  23: 'スポーツ・スポーツ観戦',
  24: '釣り',
  25: '温泉・サウナ',
  26: 'その他',
  27: 'ダイエット',
  28: 'コスメ',
  29: 'ビューティーケアアイテム',
  30: 'フィットネス',
  31: 'エステ',
  32: '美容サロン',
  52: '医療クリニック',
  33: 'その他',
  34: 'ストリートファッション',
  35: '韓国ファッション',
  36: 'モードファッション',
  37: 'カップルコーデ',
  38: '親子コーデ',
  39: 'プチプラコーデ',
  40: '綺麗めコーデ',
  41: 'ガーリーコーデ',
  42: '古着',
  43: 'ファッション小物・雑貨',
  44: 'その他',
  45: '子育て・知育',
  46: 'ガジェット',
  47: 'インテリア・暮らし',
  48: '勉強・資格勉強',
  49: 'ペット',
  50: '配信・ゲーム配信',
  51: 'その他',
} as const

export type Campaign = {
  id: number
  status: CampaignStatusKind
  name: string
  url: string
  companion: CampaignCompanionKind
  address?: string
  advAccountStatus: CampaignAdvAccountStatus
  age: CampaignAgeKind
  ageLimit: CampaignAgeLimit
  attention: string
  category: CampaignCategory
  categoryI18n: string
  summary: string
  wantInfluencer: string
  sponsorService: string
  sponsorServicePrice: number | null
  sponsorServicePriceIntax: number | null
  gender: CampaignGenderKind
  target?: string
  /**
   * 2022/04/13 11:00 以降は、キャンペーン作成時にグロスを編集するようになったので、基本的にrewardではなくグロスを表示する
   */
  gross: number
  followerMinLimit: number
  selectionMaxLimit: number
  selectionMaxLimitRemain?: number
  offerStarted?: string
  offerEnded?: string | null
  postStarted?: string
  postEnded?: string
  coverImages: CampaignCoverImage[]
  reservation: CampaignReservationKind
  companyName: string
  createdAt: Date
  desiredFollowers: CampaignDesiredFollowers
  mediaType: CampaignMediaType
  mediaTypeI18n: string
  nearestStation?: string
  location?: {
    stationName?: string | null
  } | null
  offerAutoMessage: string
  plan?: Plan
  /**
   * FIXME: キャンペーン作成以外でほぼ使ってない, APIから返してもらう値の場合はundefinedかも
   */
  projectType: CampaignProjectType
  selectionLimitOver: boolean
  campaignSnsRequirements: CampaignSnsRequirements[]
  offerCount?: CampaignOfferCount
  billingDetails?: CampaignBillingDetails
  commission: number
  needDelivery: boolean
  /**
   * インフルエンサーへの事前質問・回答（2022/03/01現在, スタッフ側で設定するしかないのでキャンペーン作成時は絶対に存在しない）
   */
  question?: string
  /**
   * 取り下げ可能フラグ（薬機法チェック審査中・承認済み時は取り下げできない）
   */
  canTakeBack?: boolean
  /**
   * collabo-marketing-apiから取得したキャンペーンの場合のみ存在（キャンペーン作成時は存在しない）
   * 新日程調整の利用フラグ。旧キャンペーンの場合はnull
   * ※キャンペーン作成時にこの値を送る必要はない
   */
  scheduleType?: CampaignScheduleType
  /**
   * collabo-marketing-apiから取得したキャンペーンの場合のみ存在（キャンペーン作成時は存在しない）
   * 新日程調整の設定済み（現在移行）のスケジュールの数。旧キャンペーンの場合は必ず0
   * ※キャンペーン作成時にこの値を送る必要はない
   */
  reservableScheduleCount?: number

  regularlyPublishedExecutionDay: number | null
  regularlyPublishedStatus: RegularlyPublishedStatus

  // 立替型で追加
  // 6/17日リリースするまではundefined
  costHandlingMethod?: LegacyCampaignCostHandlingMethod
  // インフルエンサー負担率（0, 10, 20, 30）
  influencerBurdenRate?: number | null
}

export type CampaignBillingDetails = {
  all: {
    offerCount: number
    amount: number
  }
  working: {
    offerCount: number
    amount: number
  }
  completed: {
    offerCount: number
    amount: number
  }
}

export type CampaignOfferCount = {
  approval: number
  approvalToday: number
  working: number
  completed: number
}
export type PlanSlot = {
  planSeriesId: number
  campaign?: Campaign
  plan?: Plan
  planName?: string
  approved: boolean
  hasCampaign: boolean
}

export type AdvShop = {
  id: number
  advAccountId: number
  shopName: string
  postalCode: string
  pref: string
  address1: string
  address2?: string
  nearestStation: string
  tel: string
}
export const campaignRequirementOptions = [
  {
    id: CampaignRequirementOptions.DraftCheck,
    title: '下書きチェック',
    price: 1000,
    description: '投稿前に内容をチェック・修正依頼を行うことが可能です。',
  },
  {
    id: CampaignRequirementOptions.SecondaryUse,
    title: '二次利用',
    price: 5000,
    description: 'インフルエンサーの投稿物をInstagram以外で利用が可能です。',
  },
  {
    id: CampaignRequirementOptions.SpecifiedPrText,
    title: '文言指定',
    price: 1000,
    description: '投稿するキャプションについて指定が可能です。',
  },
  {
    id: 'COMPOSITION_SPECIFICATION',
    title: '構図指定',
    price: 1000,
    description: '投稿する写真の内容について指定が可能です。',
  },
] as const

export type CampaignRequirementOptionsType = Record<(typeof campaignRequirementOptions)[number]['id'], boolean>

export const campaignRequirementCompositionOptions = [
  {
    id: CampaignRequirementOptions.CompositionInfluencerAndService,
    title: 'インフルエンサーと商品を一緒に写してほしい',
  },
  {
    id: CampaignRequirementOptions.CompositionProductOnly,
    title: '商品単体で写してほしい',
  },
  {
    id: CampaignRequirementOptions.CompositionUsageInContext,
    title: '実際に体験しているところを写してほしい',
  },
] as const

export type CampaignRequirementCompositionOptionsType = (typeof campaignRequirementCompositionOptions)[number]['id']

const TAG_MAPPING: Record<
  (typeof CampaignCategory)[keyof typeof CampaignCategory],
  Record<number, Partial<Record<LanguageType, string[]>>>
> = {
  PRODUCT: {
    // 通販での英語の対応はサポート外
    1: { [LanguageType.Japanese]: ['お取り寄せギフト', 'おうちカフェ', 'カフェ'], [LanguageType.English]: [] },
    2: { [LanguageType.Japanese]: ['グルメギフト', 'お取り寄せグルメ', 'お取り寄せ'], [LanguageType.English]: [] },
    3: { [LanguageType.Japanese]: ['通販グルメ', 'お取り寄せギフト', '洋食'], [LanguageType.English]: [] },
    4: { [LanguageType.Japanese]: ['通販グルメ', 'グルメギフト', '和食'], [LanguageType.English]: [] },
    5: { [LanguageType.Japanese]: ['お取り寄せグルメ', 'おうち焼肉', '韓国料理'], [LanguageType.English]: [] },
    6: { [LanguageType.Japanese]: ['お取り寄せグルメ', '冷凍中華', '中華'], [LanguageType.English]: [] },
    7: { [LanguageType.Japanese]: ['お取り寄せラーメン', '通販ラーメン', 'ラーメン'], [LanguageType.English]: [] },
    8: { [LanguageType.Japanese]: ['冷凍グルメ', '通販グルメ', 'お取り寄せグルメ'], [LanguageType.English]: [] },
    9: { [LanguageType.Japanese]: ['グルメギフト', 'ご褒美グルメ', 'フレンチ'], [LanguageType.English]: [] },
    10: {
      [LanguageType.Japanese]: ['通販スイーツ', 'お取り寄せスイーツ', 'おうちカフェ'],
      [LanguageType.English]: [],
    },
    11: { [LanguageType.Japanese]: ['グルメギフト', 'お取り寄せグルメ', 'アレンジ'], [LanguageType.English]: [] },
    12: { [LanguageType.Japanese]: ['ワイン通販', 'お家でワイン', 'ワイン'], [LanguageType.English]: [] },
    13: {
      [LanguageType.Japanese]: ['お取り寄せカレー', 'カレー通販', 'お取り寄せグルメ'],
      [LanguageType.English]: [],
    },
    14: { [LanguageType.Japanese]: ['海鮮グルメ', 'お取り寄せグルメ', '海鮮'], [LanguageType.English]: [] },
    15: { [LanguageType.Japanese]: ['お取り寄せグルメ', '通販グルメ', '居酒屋'], [LanguageType.English]: [] },
    16: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    17: { [LanguageType.Japanese]: ['グランピング', 'キャンプ', '旅行アイテム'], [LanguageType.English]: [] },
    18: { [LanguageType.Japanese]: ['お泊まりグッズ', 'アメニティ', 'ホテル'], [LanguageType.English]: [] },
    19: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    20: {
      [LanguageType.Japanese]: ['アウトドア用品', 'アウトドアグッズ', 'キャンプグッズ'],
      [LanguageType.English]: [],
    },
    21: { [LanguageType.Japanese]: ['ドライブ', 'カー用品', 'ドライブ用品'], [LanguageType.English]: [] },
    22: {
      [LanguageType.Japanese]: ['プライズ景品', 'アクティビティ', 'アクティビティグッズ'],
      [LanguageType.English]: [],
    },
    23: { [LanguageType.Japanese]: ['ゴルフ用品', 'スポーツ用品', 'スポーツグッズ'], [LanguageType.English]: [] },
    24: { [LanguageType.Japanese]: ['釣り餌', '釣り道具', '釣り'], [LanguageType.English]: [] },
    25: { [LanguageType.Japanese]: ['サウナハット', 'サウナグッズ', 'サウナ'], [LanguageType.English]: [] },
    26: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    27: { [LanguageType.Japanese]: ['健康ダイエット', '健康サポート', 'ダイエット'], [LanguageType.English]: [] },
    28: { [LanguageType.Japanese]: ['ボディケア', 'スキンケアグッズ', 'コスメ'], [LanguageType.English]: [] },
    29: {
      [LanguageType.Japanese]: ['おすすめスキンケア', 'スキンケア', 'ビューティーケアアイテム'],
      [LanguageType.English]: [],
    },
    30: {
      [LanguageType.Japanese]: ['フィットネス器具', 'オンラインレッスン', 'フィットネス'],
      [LanguageType.English]: [],
    },
    31: {
      [LanguageType.Japanese]: ['フェイシャルスクール', 'セルフケアレッスン', '小顔矯正スクール'],
      [LanguageType.English]: [],
    },
    32: {
      [LanguageType.Japanese]: ['カラーシャンプー', 'カラートリートメント', '美容サロン'],
      [LanguageType.English]: [],
    },
    33: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    34: {
      [LanguageType.Japanese]: ['アパレル通販', 'ストリートファッション', 'ファッション'],
      [LanguageType.English]: [],
    },
    35: {
      [LanguageType.Japanese]: ['カジュアルTシャツ', '韓国ファッション', '韓国アイテム'],
      [LanguageType.English]: [],
    },
    36: {
      [LanguageType.Japanese]: ['大人カジュアル', 'アパレル通販', 'モードファッション'],
      [LanguageType.English]: [],
    },
    37: {
      [LanguageType.Japanese]: ['ユニセックスデザイン', 'ユニセックスブランド', 'カップルコーデ'],
      [LanguageType.English]: [],
    },
    38: { [LanguageType.Japanese]: ['親子コーデ', 'おそろコーデ', '親子リンクコーデ'], [LanguageType.English]: [] },
    39: {
      [LanguageType.Japanese]: ['通販ファッション', 'カジュアルファッションコーデ', 'プチプラコーデ'],
      [LanguageType.English]: [],
    },
    40: {
      [LanguageType.Japanese]: ['きれいめコーデ', 'ファッション通販', '綺麗めコーデ'],
      [LanguageType.English]: [],
    },
    41: { [LanguageType.Japanese]: ['子供服通販', 'ガーリーコーデ', 'ガーリー'], [LanguageType.English]: [] },
    42: { [LanguageType.Japanese]: ['古着', 'リメイクオーダー', 'リメイク'], [LanguageType.English]: [] },
    43: {
      [LanguageType.Japanese]: ['アクセサリー通販', 'おすすめアクセサリー', 'ファッションアイテム'],
      [LanguageType.English]: [],
    },
    44: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    45: { [LanguageType.Japanese]: ['知育玩具', '子育てアイテム', '子育て'], [LanguageType.English]: [] },
    46: {
      [LanguageType.Japanese]: ['便利グッズ', '旅行便利グッズ', 'おすすめガジェット'],
      [LanguageType.English]: [],
    },
    47: { [LanguageType.Japanese]: ['便利グッズ', 'おすすめギフト', '通販ギフト'], [LanguageType.English]: [] },
    48: { [LanguageType.Japanese]: ['勉強', '資格', '資格勉強'], [LanguageType.English]: [] },
    49: { [LanguageType.Japanese]: ['ペット用品', 'ペットフード', 'ペットグッズ'], [LanguageType.English]: [] },
    50: { [LanguageType.Japanese]: ['ゲーム配信', 'ゲーム実況', 'ゲーム'], [LanguageType.English]: [] },
    51: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    52: { [LanguageType.Japanese]: ['にきび治療', 'オンライン診療', 'クリニック'], [LanguageType.English]: [] },
    53: { [LanguageType.Japanese]: ['味変', '簡単調味料', 'エスニック料理'], [LanguageType.English]: [] },
  },
  SERVICE: {
    1: {
      [LanguageType.Japanese]: ['カフェ', 'cafe', 'おしゃれスポット'],
      [LanguageType.English]: ['Cafe', 'Coffeeshop', 'StylishSpot'],
    },
    2: {
      [LanguageType.Japanese]: ['テイクアウトスイーツ', 'テイクアウト', '食べ歩き'],
      [LanguageType.English]: ['TakeoutSweets', 'Takeout', 'EatingWhileWalking'],
    },
    3: {
      [LanguageType.Japanese]: ['グルメ', '洋食', '洋食屋'],
      [LanguageType.English]: ['Gourmet', 'WesternFood', 'WesternRestaurant'],
    },
    4: {
      [LanguageType.Japanese]: ['グルメ', '和食', '和食屋'],
      [LanguageType.English]: ['Gourmet', 'JapaneseFood', 'JapaneseRestaurant'],
    },
    5: {
      [LanguageType.Japanese]: ['焼肉', '韓国料理', '韓国グルメ'],
      [LanguageType.English]: ['Yakiniku', 'KoreanCuisine', 'KoreanGourmet'],
    },
    6: {
      [LanguageType.Japanese]: ['中華', 'グルメ', '中華料理'],
      [LanguageType.English]: ['Chinese', 'Gourmet', 'ChineseCuisine'],
    },
    7: {
      [LanguageType.Japanese]: ['ラーメン', 'らーめん', 'ramen'],
      [LanguageType.English]: ['Ramen', 'Ramenshop', 'Ramenstore'],
    },
    8: {
      [LanguageType.Japanese]: ['イタリアン', 'ディナー', 'ワイン'],
      [LanguageType.English]: ['Italian', 'Dinner', 'Wine'],
    },
    9: {
      [LanguageType.Japanese]: ['フレンチ', 'フレンチコース', 'おしゃれスポット'],
      [LanguageType.English]: ['French', 'FrenchCourse', 'StylishSpot'],
    },
    10: {
      [LanguageType.Japanese]: ['スイーツ', 'カフェ', 'おしゃれスポット'],
      [LanguageType.English]: ['Sweets', 'Cafe', 'StylishSpot'],
    },
    11: {
      [LanguageType.Japanese]: ['手料理', 'アレンジレシピ', 'アレンジ料理'],
      [LanguageType.English]: ['HomeCooking', 'ArrangementRecipe', 'ArrangementDish'],
    },
    12: {
      [LanguageType.Japanese]: ['バー', 'bar', 'おしゃれスポット'],
      [LanguageType.English]: ['Bar', 'liqueur', 'StylishSpot'],
    },
    13: {
      [LanguageType.Japanese]: ['カレー', 'curry', 'おすすめカレー'],
      [LanguageType.English]: ['Curry', 'Curryshop', 'RecommendedCurry'],
    },
    14: {
      [LanguageType.Japanese]: ['グルメ', '海鮮', '海鮮グルメ'],
      [LanguageType.English]: ['Gourmet', 'Seafood', 'SeafoodGourmet'],
    },
    15: {
      [LanguageType.Japanese]: ['グルメ', '居酒屋', 'お酒'],
      [LanguageType.English]: ['Gourmet', 'Izakaya', 'Alcohol'],
    },
    16: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    17: {
      [LanguageType.Japanese]: ['グランピング', 'キャンプ', '旅行'],
      [LanguageType.English]: ['Glamping', 'Camping', 'Travel'],
    },
    18: { [LanguageType.Japanese]: ['旅行', 'ホテル', '旅'], [LanguageType.English]: ['Travel', 'Hotel', 'Journey'] },
    19: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    20: {
      [LanguageType.Japanese]: ['bbq', 'ランチ', 'アウトドア'],
      [LanguageType.English]: ['BBQ', 'Lunch', 'Outdoor'],
    },
    21: {
      [LanguageType.Japanese]: ['レンタカー', 'おでかけ', 'ドライブ'],
      [LanguageType.English]: ['RentalCar', 'Outdoor', 'Drive'],
    },
    22: {
      [LanguageType.Japanese]: ['映えスポット', 'アクティビティ', 'おでかけ'],
      [LanguageType.English]: ['InstagrammableSpot', 'Activity', 'Outing'],
    },
    23: {
      [LanguageType.Japanese]: ['スポーツ', 'スポーツ観戦', '観戦'],
      [LanguageType.English]: ['Sports', 'SportsWatching', 'Watching'],
    },
    24: {
      [LanguageType.Japanese]: ['釣りアイテム', '釣り堀', '釣り'],
      [LanguageType.English]: ['FishingItems', 'FishingPond', 'Fishing'],
    },
    25: {
      [LanguageType.Japanese]: ['プライベートサウナ', '貸切サウナ', 'サウナ'],
      [LanguageType.English]: ['PrivateSauna', 'PrivateRentalSauna', 'Sauna'],
    },
    26: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    27: {
      [LanguageType.Japanese]: ['ジム', 'パーソナルトレーニング', 'ダイエット'],
      [LanguageType.English]: ['Gym', 'PersonalTraining', 'Diet'],
    },
    28: {
      [LanguageType.Japanese]: ['香水', 'オリジナル香水', 'コスメ'],
      [LanguageType.English]: ['Perfume', 'OriginalPerfume', 'Cosmetics'],
    },
    29: {
      [LanguageType.Japanese]: ['スキンケア', '時短ケア', 'おすすめアイテム'],
      [LanguageType.English]: ['Skincare', 'TimeSavingCare', 'RecommendedItems'],
    },
    30: {
      [LanguageType.Japanese]: ['パーソナルトレーニングジム', 'フィットネスジム', 'フィットネス'],
      [LanguageType.English]: ['PersonalTrainingGym', 'FitnessGym', 'Fitness'],
    },
    31: {
      [LanguageType.Japanese]: ['エステサロン', '全身整体', 'エステ'],
      [LanguageType.English]: ['BeautySalon', 'FullBodyTreatment', 'Esthetics'],
    },
    32: {
      [LanguageType.Japanese]: ['サロン', 'セルフホワイトニングサロン', '美容サロン'],
      [LanguageType.English]: ['Salon', 'SelfWhiteningSalon', 'BeautySalon'],
    },
    33: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    34: {
      [LanguageType.Japanese]: ['ストリートファッション', 'ストリート', '古着'],
      [LanguageType.English]: ['StreetFashion', 'Street', 'VintageClothing'],
    },
    35: {
      [LanguageType.Japanese]: ['韓国ファッション', '韓国コーデ', '韓国アイテム'],
      [LanguageType.English]: ['KoreanFashion', 'KoreanCoordinate', 'KoreanItems'],
    },
    36: {
      [LanguageType.Japanese]: ['セレクトショップ', 'モードファッション', 'おすすめアイテム'],
      [LanguageType.English]: ['SelectShop', 'ModeFashion', 'RecommendedItems'],
    },
    37: {
      [LanguageType.Japanese]: ['カップルコーデ', 'おそろコーデ', 'カップル'],
      [LanguageType.English]: ['CoupleCoordinate', 'MatchingCoordinate', 'Couple'],
    },
    38: {
      [LanguageType.Japanese]: ['親子コーデ', 'おそろコーデ', '親子リンクコーデ'],
      [LanguageType.English]: ['ParentChildCoordinate', 'MatchingCoordinate', 'ParentChildLinkCoordinate'],
    },
    39: {
      [LanguageType.Japanese]: ['プチプラコーデ', 'プチプラ', 'おすすめアイテム'],
      [LanguageType.English]: ['AffordableCoordinate', 'Affordable', 'RecommendedItems'],
    },
    40: {
      [LanguageType.Japanese]: ['きれいめコーデ', 'シンプルコーデ', '綺麗めコーデ'],
      [LanguageType.English]: ['NeatCoordinate', 'SimpleCoordinate', 'NeatCoordinate'],
    },
    41: {
      [LanguageType.Japanese]: ['アクセサリーショップ', 'ハンドメイドショップ', 'ガーリーコーデ'],
      [LanguageType.English]: ['AccessoryShop', 'HandmadeShop', 'GirlyCoordinate'],
    },
    42: {
      [LanguageType.Japanese]: ['ヴィンテージショップ', '古着', '古着コーデ'],
      [LanguageType.English]: ['VintageShop', 'UsedClothing', 'UsedClothingCoordinate'],
    },
    43: {
      [LanguageType.Japanese]: ['セレクトショップ', '雑貨', 'おすすめアイテム'],
      [LanguageType.English]: ['SelectShop', 'MiscellaneousGoods', 'RecommendedItems'],
    },
    44: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    45: {
      [LanguageType.Japanese]: ['子連れスポット', '習い事', '知育'],
      [LanguageType.English]: ['ChildFriendlySpot', 'Lessons', 'EarlyChildhoodEducation'],
    },
    46: {
      [LanguageType.Japanese]: ['ガジェット', '便利グッズ', 'おすすめアイテム'],
      [LanguageType.English]: ['Gadget', 'ConvenientGoods', 'RecommendedItems'],
    },
    47: {
      [LanguageType.Japanese]: ['インテリア', '暮らし', 'ライフスタイル'],
      [LanguageType.English]: ['Interior', 'Living', 'Lifestyle'],
    },
    48: {
      [LanguageType.Japanese]: ['シェアオフィス', 'コワーキングスペース', '勉強'],
      [LanguageType.English]: ['SharedOffice', 'CoworkingSpace', 'Study'],
    },
    49: {
      [LanguageType.Japanese]: ['トリミングサロン', 'トリミング', 'ペット'],
      [LanguageType.English]: ['TrimmingSalon', 'Trimming', 'Pet'],
    },
    50: {
      [LanguageType.Japanese]: ['写真館', '写真', '記念撮影'],
      [LanguageType.English]: ['PhotoStudio', 'Photo', 'CommemorativePhotoShoot'],
    },
    51: { [LanguageType.Japanese]: [], [LanguageType.English]: [] },
    52: {
      [LanguageType.Japanese]: ['美容', '医療ホワイトニング', 'クリニック'],
      [LanguageType.English]: ['Beauty', 'MedicalWhitening', 'Clinic'],
    },
    53: {
      [LanguageType.Japanese]: ['タイ料理', 'エスニック料理', 'グルメ'],
      [LanguageType.English]: ['ThaiCuisine', 'EthnicCuisine', 'Gourmet'],
    },
  },
  EVENT: {},
}

export function getDefaultTags(category: CampaignCategory, searchSubGenreIds: number[], language: LanguageType) {
  const categoryMapping = TAG_MAPPING[category]
  const allTags = searchSubGenreIds.flatMap((sub) => categoryMapping[sub]?.[language] ?? [])

  // 重複を削除
  const uniqueTags = [...new Set(allTags)]
  return uniqueTags
}
